import { useEffect, useMemo } from "react";
import { FloorplannerStore } from "../../../store/floorplannerStore";
import { SymbolType } from "../../../types/floorplannerTypes";
import { useFloorplannerProduct3DAssets } from "../catalog/catalogQueries";
import {
  hydrateProduct3DReference,
  selectHydratableProduct3DAsset,
  shouldHydrateProduct3DReference,
} from "./product3DAssetHydration";
import { Product3DAssetReference } from "./product3DTypes";

export function useHydratePlacedProduct3DAssets(input: {
  enabled: boolean;
  symbols: SymbolType[];
  store: FloorplannerStore;
}) {
  const productIds = useMemo(
    () =>
      Array.from(
        new Set(
          input.symbols
            .map((symbol) => symbol.threeD?.product?.productId)
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [input.symbols],
  );
  const assetsQuery = useFloorplannerProduct3DAssets(
    productIds,
    !input.enabled || productIds.length === 0,
  );

  useEffect(() => {
    if (!input.enabled) return;
    const readyAssets = (assetsQuery.data?.product3DAssets ??
      []) as Product3DAssetReference[];
    if (readyAssets.length === 0) return;

    const assetsByProductId = new Map<string, Product3DAssetReference[]>();
    readyAssets.forEach((asset) => {
      if (!asset.productId) return;
      const assets = assetsByProductId.get(asset.productId) ?? [];
      assets.push(asset);
      assetsByProductId.set(asset.productId, assets);
    });

    input.symbols.forEach((symbol) => {
      const product = symbol.threeD?.product;
      if (!product?.productId) return;
      const asset = selectHydratableProduct3DAsset(
        product,
        assetsByProductId.get(product.productId) ?? [],
      );
      if (!asset || !shouldHydrateProduct3DReference(product, asset)) return;
      input.store.symbolsMap.set(symbol.id, {
        ...symbol,
        threeD: {
          ...symbol.threeD,
          product: hydrateProduct3DReference(product, asset),
        },
      });
    });
  }, [assetsQuery.data, input.enabled, input.store, input.symbols]);
}
